import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CrawlRunHistoryService,
  type CrawlRunSiteComparisonPayload,
  type CrawlRunSiteDiff,
  type CrawlRunSiteDiffBucketView,
  type CrawlRunSiteDiffEntry,
  type SiteDiffBucket
} from '../../services/crawl-run-history.service';
import type { CrawlRunRef } from '../../services/url-run-comparison.service';

/**
 * Crawl history and site-level diff on Crawl Management — FW-CRAWL-DELTA-01
 * phase 5. Spec: docs/features/MONITORING_CRAWL_DELTAS.md §6 phase 5.
 *
 * **Its own component for its own stylesheet**, the same reason the phase-3
 * comparison is one: `crawl-management.css` shares the 90 KB `anyComponentStyle`
 * budget that `url-detail.css` already overshoots (TECHNICAL_DEBT §0t), and the
 * budget is per component. A rule added here costs nothing.
 *
 * Every string on screen comes from the backend — `headline`, each bucket's
 * `description`, each entry's `display`. The component never subtracts, never
 * formats a delta and never invents copy for a bucket, because the difference
 * between "reworked" and "the footer changed on every page" is a rule that lives
 * with the thresholds that decided it, not in a template.
 */

/** The buckets shown as headline tiles, in the order an operator acts on them.
 *  `unchanged` and `not-compared` are real answers but not work, so they stay in
 *  the detail lists below rather than competing for the top of the panel. */
const HEADLINE_BUCKETS: SiteDiffBucket[] = ['new', 'removed', 'reworked', 'minor-change'];

/** Bucket -> the `.card--edge` tone its tile paints. Partial on purpose: the
 *  buckets that are not work carry no status colour, and fall through to the
 *  neutral hook in `bucketClass()`. */
const BUCKET_TONE: Partial<Record<SiteDiffBucket, string>> = {
  new: 'card--success',
  removed: 'card--danger',
  reworked: 'card--warning'
};

@Component({
  selector: 'app-crawl-run-history',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './crawl-run-history.html',
  styleUrl: './crawl-run-history.css'
})
export class CrawlRunHistoryComponent implements OnChanges {
  @Input() domain: string | null = null;

  loading = false;
  error: string | null = null;
  payload: CrawlRunSiteComparisonPayload | null = null;
  /** Which run's diff is on screen. Null means the newest. */
  selectedRunId: string | null = null;
  expanded = false;

  constructor(private history: CrawlRunHistoryService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['domain']) {
      this.payload = null;
      this.selectedRunId = null;
      this.expanded = false;
      this.load();
    }
  }

  load(): void {
    const domain = (this.domain || '').trim();
    this.error = null;
    if (!domain) return;

    this.loading = true;
    this.history.getForDomain(domain, this.selectedRunId).subscribe({
      next: (response) => {
        this.payload = response?.data ?? null;
        this.loading = false;
      },
      error: () => {
        this.error = 'Crawl history could not be loaded.';
        this.loading = false;
      }
    });
  }

  /** Selecting a run picks which run is compared. It never picks what that run
   *  is compared against: the backend always uses the run's frozen predecessor. */
  selectRun(run: CrawlRunRef): void {
    this.selectedRunId = run.crawlRunId;
    this.load();
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  hasRuns(): boolean {
    return !!this.payload && this.payload.runsCaptured > 0;
  }

  diff(): CrawlRunSiteDiff | null {
    return this.payload?.diff ?? null;
  }

  /** True only when the diff actually compared two runs. Everything else — a
   *  first run, a format wall, a run whose rows went missing — must render its
   *  headline and nothing numeric. */
  hasComparison(): boolean {
    return this.diff()?.state === 'compared';
  }

  currentRunId(): string | null {
    return this.diff()?.currentRun?.crawlRunId ?? this.payload?.runs?.[0]?.crawlRunId ?? null;
  }

  /** The tiles. Kept in `HEADLINE_BUCKETS` order rather than the backend's, and
   *  a bucket the backend did not return is simply absent rather than zero. */
  headlineBuckets(): CrawlRunSiteDiffBucketView[] {
    const buckets = this.diff()?.buckets ?? [];
    return HEADLINE_BUCKETS.map((bucket) => buckets.find((b) => b.bucket === bucket)).filter(
      (b): b is CrawlRunSiteDiffBucketView => !!b
    );
  }

  /** The lists worth opening: everything with at least one URL in it. A bucket
   *  with a count and no entries would read as an empty answer rather than as a
   *  list the backend capped, so `truncated` is rendered beside it. */
  listedBuckets(): CrawlRunSiteDiffBucketView[] {
    return (this.diff()?.buckets ?? []).filter((b) => b.entries.length > 0);
  }

  /** The tile's `.card--edge` tone. A bucket that means work gets a status
   *  tone; the rest get the neutral hook, which repaints the stripe rather
   *  than dropping it — see the note in the stylesheet. */
  bucketClass(bucket: SiteDiffBucket): string {
    return BUCKET_TONE[bucket] ?? 'run-site-bucket--neutral';
  }

  entryNote(entry: CrawlRunSiteDiffEntry): string | null {
    if (entry.tracked === false) return 'excluded from the tracked inventory';
    return null;
  }

  runLabel(run: CrawlRunRef | null): string {
    if (!run) return 'none';
    return (run.completedAt || '').slice(0, 10) || run.crawlRunId;
  }

  /** Never a blank cell: a run with no coverage recorded says so, because a
   *  missing coverage pair is unknown rather than zero. */
  runQualifier(run: CrawlRunRef): string {
    const bits: string[] = [];
    if (typeof run.urlsCaptured === 'number') bits.push(`${run.urlsCaptured} URLs`);
    if (typeof run.urlsFailed === 'number' && run.urlsFailed > 0) bits.push(`${run.urlsFailed} failed`);
    if (typeof run.coverageExpected === 'number' && typeof run.coverageAnalyzed === 'number') {
      bits.push(`coverage ${run.coverageAnalyzed}/${run.coverageExpected}`);
    } else {
      bits.push('coverage not recorded');
    }
    if (run.crawlMode) bits.push(run.crawlMode);
    if (run.backfilled) bits.push('backfilled');
    if (run.captureTrigger) bits.push(run.captureTrigger);
    return bits.join(' · ');
  }
}
